// src/env.ts
/**
 * Build a full URL from optional protocol, host, and port env vars.
 * - If protocolEnv is undefined, infer from port (443→https, else http).
 * - Drops the port if it's the default for that protocol.
 */
export function buildUrl(protocolEnv: string | undefined, host: string, port: string): string {
  // infer protocol if not explicitly set
  const protocol = protocolEnv ?? (port === '443' ? 'https' : 'http');

  // default port mapping
  const defaultPort = protocol === 'https' ? '443' : '80';

  // include port only if it's non-default
  const portSegment = port === defaultPort ? '' : `:${port}`;

  return `${protocol}://${host}${portSegment}`;
}

export class MissingEnvError extends Error {
  constructor(public readonly name: string) {
    super(`Missing required environment variable: ${name}`);
    this.name = 'MissingEnvError';
  }
}

type EnvSource = Record<string, string | undefined>;

export function requireEnv(name: string, source: EnvSource = import.meta.env): string {
  const value = source[name];
  if (value === undefined || value === '') {
    throw new MissingEnvError(name);
  }
  return value;
}

export function optionalEnv(name: string, fallback: string, source: EnvSource = import.meta.env): string {
  const value = source[name];
  if (value === undefined || value === '') {
    return fallback;
  }
  return value;
}

const {
  VITE_PUBLIC_API_PROTOCOL,
  VITE_PUBLIC_WEBSITE_PROTOCOL,
  VITE_PUBLIC_WEBSITE_HOST,
  VITE_PUBLIC_WEBSITE_PORT,
  VITE_PUBLIC_WEBTRANSPORT_CERT_HASH,
  VITE_PUBLIC_WEBTRANSPORT_HOST,
  VITE_PUBLIC_WEBTRANSPORT_PORT,
  VITE_DEV_LOGIN_ENABLED,
} = import.meta.env as EnvSource;

const API_HOST_VALUE = requireEnv('VITE_PUBLIC_API_HOST');

export const API_URL = buildUrl(VITE_PUBLIC_API_PROTOCOL, API_HOST_VALUE, requireEnv('VITE_PUBLIC_API_PORT'));
export const API_HOST = API_HOST_VALUE;

export const WEBSITE_URL = buildUrl(
  VITE_PUBLIC_WEBSITE_PROTOCOL,
  VITE_PUBLIC_WEBSITE_HOST ?? '',
  VITE_PUBLIC_WEBSITE_PORT ?? '',
);

export const POSTHOG_HOST = optionalEnv('VITE_PUBLIC_POSTHOG_HOST', '');
export const POSTHOG_KEY = optionalEnv('VITE_PUBLIC_POSTHOG_KEY', '');

export const SFU_URL = requireEnv('VITE_PUBLIC_SFU_URL');

export const OAUTH_CLIENT_ID = optionalEnv('VITE_PUBLIC_OAUTH_CLIENT_ID', 'aircast-web');

export const WATCH_BASE_URL =
  optionalEnv('VITE_PUBLIC_WATCH_BASE_URL', '') ||
  (typeof window !== 'undefined' ? window.location.origin : '');

export const COOKIE_NAME = optionalEnv('VITE_PUBLIC_COOKIE_NAME', 'session_token');

export const WEBTRANSPORT_CERT_HASH = VITE_PUBLIC_WEBTRANSPORT_CERT_HASH;

export const WEBTRANSPORT_HOST = VITE_PUBLIC_WEBTRANSPORT_HOST || API_HOST_VALUE;

export const WEBTRANSPORT_PORT = VITE_PUBLIC_WEBTRANSPORT_PORT;

export const DEV_LOGIN_ENABLED = import.meta.env.DEV || VITE_DEV_LOGIN_ENABLED === 'true';
